import { BRAND_DEFAULT, NEUTRAL, SEMANTIC, STATUS } from './color.js';
import { ELEVATION } from './elevation.js';
import { MOTION } from './motion.js';
import { RADIUS } from './radius.js';
import { SIZE, SPACE, SPACE_BASE } from './space.js';
import { FONT_FAMILY, TYPE } from './type.js';
import { BREAKPOINTS } from './breakpoints.js';
/** Prefix for every custom property this package emits. */
export const CSS_PREFIX = '--sp';
/** The custom properties that change with colour mode. */
export function modeVariables(mode) {
    const vars = {};
    for (const [k, v] of Object.entries({ ...NEUTRAL[mode] }))
        vars[`${CSS_PREFIX}-color-${kebab(k)}`] = v;
    for (const [tone, pair] of Object.entries(SEMANTIC[mode])) {
        vars[`${CSS_PREFIX}-color-${tone}`] = pair.fg;
        vars[`${CSS_PREFIX}-color-${tone}-bg`] = pair.bg;
    }
    for (const [hue, v] of Object.entries(STATUS[mode]))
        vars[`${CSS_PREFIX}-status-${hue}`] = v;
    for (const [k, v] of Object.entries({ ...ELEVATION[mode] }))
        vars[`${CSS_PREFIX}-shadow-${k}`] = v;
    return vars;
}
/** The custom properties that are the same in both modes. */
export function staticVariables() {
    const vars = {
        [`${CSS_PREFIX}-brand-primary`]: BRAND_DEFAULT.primary,
        [`${CSS_PREFIX}-brand-secondary`]: BRAND_DEFAULT.secondary,
        [`${CSS_PREFIX}-font-sans`]: FONT_FAMILY.sans,
        [`${CSS_PREFIX}-font-mono`]: FONT_FAMILY.mono,
        [`${CSS_PREFIX}-space-base`]: `${SPACE_BASE}px`,
        [`${CSS_PREFIX}-space-section-gap`]: `${SPACE.sectionGap}px`,
        [`${CSS_PREFIX}-space-card-padding`]: `${SPACE.cardPadding.desktop}px`,
        [`${CSS_PREFIX}-space-page-gutter`]: `${SPACE.pageGutter.desktop}px`,
        [`${CSS_PREFIX}-size-sidebar`]: `${SIZE.sidebarWidth}px`,
        [`${CSS_PREFIX}-size-content-max`]: `${SIZE.contentMaxWidth}px`,
        [`${CSS_PREFIX}-size-control`]: `${SIZE.control.medium}px`,
        [`${CSS_PREFIX}-motion-fast`]: `${MOTION.duration.fast}ms`,
        [`${CSS_PREFIX}-motion-base`]: `${MOTION.duration.base}ms`,
        [`${CSS_PREFIX}-motion-easing`]: MOTION.easing,
    };
    for (const [k, v] of Object.entries(RADIUS))
        vars[`${CSS_PREFIX}-radius-${k}`] = `${v}px`;
    for (const [role, t] of Object.entries(TYPE)) {
        vars[`${CSS_PREFIX}-type-${kebab(role)}-size`] = `${t.size}px`;
        vars[`${CSS_PREFIX}-type-${kebab(role)}-weight`] = String(t.weight);
    }
    for (const [k, v] of Object.entries(BREAKPOINTS))
        vars[`${CSS_PREFIX}-bp-${k}`] = `${v}px`;
    return vars;
}
function block(selector, vars, indent = '') {
    const lines = Object.entries(vars).map(([k, v]) => `${indent}  ${k}: ${v};`);
    return `${indent}${selector} {\n${lines.join('\n')}\n${indent}}`;
}
/**
 * The whole token set as a stylesheet: light on `:root`, dark under `[data-theme="dark"]`
 * and under the OS preference when no explicit theme is set. Emitted to `dist/tokens.css`
 * at build; also injected by `SprocketThemeProvider` so non-MUI markup can use the tokens.
 */
export function tokensCss() {
    const light = { ...staticVariables(), ...modeVariables('light') };
    const dark = modeVariables('dark');
    return [
        block(':root', light),
        block(':root[data-theme="dark"]', dark),
        `@media (prefers-color-scheme: dark) {\n${block(':root:not([data-theme="light"])', dark, '  ')}\n}`,
    ].join('\n\n');
}
function kebab(s) {
    return s.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
}
//# sourceMappingURL=css.js.map